import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/ui/Layout';
import NoIndexHelmet from '../../components/seo/NoIndexHelmet';
import { useAuth } from '../../components/context/AuthContext';
import { calculateDealPrice } from '../../utils/pricing/dealPricing';
import { useNotification } from '../../components/ui/Notification';
import { db } from '../../firebase';
import { doc, onSnapshot, updateDoc, deleteField, serverTimestamp, runTransaction, getDoc, Timestamp, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { useNavigate, Link } from 'react-router-dom';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import GctSummaryBreakdown from '../../components/tax/GctSummaryBreakdown';
import { Button, Row, Col, Alert, Container, Card, ListGroup, Badge, Form, Image, Toast, ToastContainer, Spinner } from 'react-bootstrap';
import NotificationPermission from '../../components/ui/NotificationPermission';
import { showCartUpdateNotification } from '../../utils/cartHelpers';
import { isIOSOrMobileSafari } from '../../utils/browserUtils';
import './Carts/Cart.css';
import { createLogger } from '../../utils/logger';
import SupermarketDropdown from '../../components/cart/SupermarketDropdown';
import PickupVerificationQrModal from '../../components/cart/PickupVerificationQrModal';
import ShoppingListFormModal from '../../components/shoppingList/ShoppingListFormModal';
import { getCartStatusBadge } from '../../utils/cartStatusDisplay';
import {
  resolveCartDeliveryOption,
  shouldShowPickupVerificationQr,
} from '../../utils/cartDeliveryOption';
import { StatusHelpers } from '../../constants/cartStatus';
import { calculateCartGctBreakdown } from '../../utils/pricing/gctTax';

const logger = createLogger('Carts');

export default function Carts() {
  const { currentUser, userName } = useAuth();
  const { showNotification } = useNotification();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [cartFilters, setCartFilters] = useState({}); // { [supermarketId]: statusString }
  const [supermarketFilter, setSupermarketFilter] = useState('');
  const [productImages, setProductImages] = useState({}); // { [productId]: imageUrl }
  const [productDetails, setProductDetails] = useState({}); // { [productId]: productData }
  const [inventoryDetails, setInventoryDetails] = useState({}); // { [productId]: inventoryData }
  const [showNotificationMessage, setShowNotificationMessage] = useState(false);
  const [viewedCartInstances, setViewedCartInstances] = useState(new Set()); // Track which cart instances user has viewed
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [confirmingOrder, setConfirmingOrder] = useState(null); // Stores the ID of the order being confirmed
  const [paginationState, setPaginationState] = useState({}); // Pagination state for all filters
  const [ordersPerPage] = useState(5); // Number of orders per page for all filters
  const [supermarkets, setSupermarkets] = useState({}); // { [supermarketId]: supermarketData }
  const [localQuantities, setLocalQuantities] = useState({}); // Store local input values for typing
  const [saveListContext, setSaveListContext] = useState(null); // { supermarketId, supermarketName, products } for the shopping list modal
  const [pickupQrCartInstanceId, setPickupQrCartInstanceId] = useState(null);

  const navigate = useNavigate();

  // Helper function to get current input value (local state or product quantity)
  const getCurrentQuantity = useCallback((productId, instanceId, supermarketId) => {
    const localKey = `${supermarketId}_${instanceId}_${productId}`;
    return localQuantities[localKey];
  }, [localQuantities]);

  // Function to calculate deal price for a product
  const calculateDealPriceForProduct = useCallback((productId, quantity, regularPrice, supermarketId) => {
    const inventoryKey = `${productId}_${supermarketId}`;
    return calculateDealPrice({
      quantity,
      regularPrice,
      inventoryData: inventoryDetails[inventoryKey],
    });
  }, [inventoryDetails]);

  // Function to fetch supermarket details
  const fetchSupermarketDetails = useCallback(async (supermarketId) => {
    if (supermarkets[supermarketId]) return; // Already fetched
    
    try {
      const supermarketDoc = await getDoc(doc(db, 'Supermarkets', supermarketId));
      if (supermarketDoc.exists()) {
        const supermarketData = { id: supermarketDoc.id, ...supermarketDoc.data() };
        setSupermarkets(prev => ({ ...prev, [supermarketId]: supermarketData }));
      }
    } catch (error) {
      logger.error(`Failed to fetch supermarket details for ${supermarketId}:`, error);
    }
  }, [supermarkets]);

  // Effect to fetch supermarket details when cart changes
  useEffect(() => {
    if (!cart) return;
    
    const supermarketIds = new Set();
    Object.keys(cart).forEach(supermarketId => {
      if (supermarketId === 'lastGlobalUpdate' || typeof cart[supermarketId] !== 'object') return;
      supermarketIds.add(supermarketId);
    });
    
    // Fetch details for all supermarkets
    supermarketIds.forEach(supermarketId => {
      fetchSupermarketDetails(supermarketId);
    });
  }, [cart, fetchSupermarketDetails]);

  // Function to mark a cart instance as viewed - wrapped in useCallback to prevent re-creation on every render
  const markCartInstanceAsViewed = useCallback((instanceId) => {
    if (!viewedCartInstances.has(instanceId)) {
      setViewedCartInstances(prev => new Set([...prev, instanceId]));
      // logger.debug(`[Carts.js] Marked cart instance ${instanceId} as viewed.`);
    }
  }, [viewedCartInstances]);

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { label: "Your Cart" }
  ];

  // Effect to check for unseen updates and show notification message
  useEffect(() => {
    if (!currentUser || !cart) return;

    // Check if there are any unseen updates
    let hasUnseenUpdates = false;
    const newlyAddedProducts = [];
    
    for (const supermarketId in cart) {
      if (supermarketId === 'lastGlobalUpdate' || typeof cart[supermarketId] !== 'object') continue;
      const supermarketInstances = cart[supermarketId];
      
      for (const instanceId in supermarketInstances) {
        if (supermarketInstances.hasOwnProperty(instanceId)) {
          const instance = supermarketInstances[instanceId];
          
          if (instance && instance.hasUnseenUpdate === true) {
            hasUnseenUpdates = true;
            // Check if this is a new cart instance or has new products
            if (instance.status === 'active' && instance.products && instance.products.length > 0) {
              newlyAddedProducts.push({
                supermarketName: instance.supermarketName || 'Unknown Supermarket',
                productCount: instance.products.length,
                products: instance.products
              });
            }
          }
        }
      }
    }

    // Show toast notification for newly added products
    if (newlyAddedProducts.length > 0) {
      const totalProducts = newlyAddedProducts.reduce((sum, item) => sum + item.productCount, 0);
      const supermarketNames = [...new Set(newlyAddedProducts.map(item => item.supermarketName))];
      
      let message = '';
      if (supermarketNames.length === 1) {
        message = `${totalProducts} new product${totalProducts > 1 ? 's' : ''} added to your cart at ${supermarketNames[0]}!`;
      } else {
        message = `${totalProducts} new product${totalProducts > 1 ? 's' : ''} added to your cart across ${supermarketNames.length} supermarket${supermarketNames.length > 1 ? 's' : ''}!`;
      }
      
      setToastMessage(message);
      setShowToast(true);
    }

    // Show notification message if there are unseen updates
    if (hasUnseenUpdates) {
      setShowNotificationMessage(true);
      // Auto-hide the message after 8 seconds
      const timer = setTimeout(() => {
        setShowNotificationMessage(false);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [cart, currentUser]);

  // Effect to mark cart instances as viewed when they are rendered with unseen updates
  useEffect(() => {
    if (!cart) return;
    
    // Mark cart instances as viewed when they are rendered and have unseen updates
    for (const supermarketId in cart) {
      if (supermarketId === 'lastGlobalUpdate' || typeof cart[supermarketId] !== 'object') continue;
      const supermarketInstances = cart[supermarketId];
      for (const instanceId in supermarketInstances) {
        if (supermarketInstances.hasOwnProperty(instanceId)) {
          const instance = supermarketInstances[instanceId];
          if (instance && instance.hasUnseenUpdate === true) {
            // Mark as viewed after a short delay to allow user to see the highlighting
            setTimeout(() => {
              markCartInstanceAsViewed(instanceId);
            }, 2000); // 2 second delay to allow user to see the highlighting
          }
        }
      }
    }
  }, [cart, markCartInstanceAsViewed]);

  // Effect to clear unseen updates when component mounts and cart data is available
  useEffect(() => {
    if (!currentUser || !cart) return;

    const clearFlags = async () => {
      const cartRef = doc(db, 'Carts', currentUser.uid);
      const updatesToMake = {};
      let needsFirestoreUpdate = false;

      for (const supermarketId in cart) {
        if (supermarketId === 'lastGlobalUpdate' || typeof cart[supermarketId] !== 'object') continue;
        const supermarketInstances = cart[supermarketId];
        for (const instanceId in supermarketInstances) {
          if (supermarketInstances.hasOwnProperty(instanceId)) {
            const instance = supermarketInstances[instanceId];
            // Only clear flags for cart instances that the user has viewed
            if (instance && instance.hasUnseenUpdate === true && viewedCartInstances.has(instanceId)) {
              updatesToMake[`${supermarketId}.${instanceId}.hasUnseenUpdate`] = deleteField();
              needsFirestoreUpdate = true;
              // logger.debug(`[Carts.js] Marking instance ${instanceId} as seen (user has viewed it).`);
            }
          }
        }
      }

      if (needsFirestoreUpdate) {
        updatesToMake.lastGlobalUpdate = serverTimestamp();
        try {
          await updateDoc(cartRef, updatesToMake);
          // logger.debug("[Carts.js] Successfully cleared unseen update flags for viewed cart instances in Firestore.");
        } catch (error) {
          logger.error("[Carts.js] Error clearing unseen update flags in Firestore:", error);
        }
      }
    };

    // Call clearFlags periodically to clear flags for viewed cart instances
    const timer = setInterval(clearFlags, 15000); // Check every 15 seconds
    return () => clearInterval(timer);

  }, [cart, currentUser, viewedCartInstances]); // Runs when cart data, user, or viewed instances change


  useEffect(() => {
    if (currentUser) {
      setLoading(true); // Set loading true when starting to listen or user changes
      const cartRef = doc(db, 'Carts', currentUser.uid);
      const unsubscribe = onSnapshot(cartRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            setCart(docSnapshot.data());
          } else {
            setCart(null);
          }
          setLoading(false);
        },
        (error) => {
          // Handle permission errors gracefully
          if (error.code === 'permission-denied') {
            logger.warn("[Carts.js] Cart access permission denied - user may not be authenticated");
            setCart(null);
          } else {
            logger.error("[Carts.js] Error fetching cart: ", error);
          }
          setLoading(false);
        }
      );
      return () => unsubscribe();
    } else {
      setLoading(false);
      setCart(null);
    }
  }, [currentUser]);

  // ... (handleRemoveItem, handleChangeQuantity, handleCancelPaymentAndResetCart remain the same) ...
  const handleRemoveItem = async (supermarketId, cartInstanceId, productId) => {
    if (!currentUser) return;
    const cartRef = doc(db, 'Carts', currentUser.uid);
    try {
      let removedItemName = '';
      let updatedCartData = null;
      
      await runTransaction(db, async (transaction) => {
        const cartDoc = await transaction.get(cartRef);
        if (!cartDoc.exists()) return;
        const cartData = cartDoc.data();
        if (!cartData || !cartData[supermarketId] || !cartData[supermarketId][cartInstanceId]) return;
        const currentInstance = cartData[supermarketId][cartInstanceId];
        if (currentInstance.status !== 'active') {
            alert("Only items in an 'active' cart can be modified."); return;
        }
        
        // Find the item being removed to get its name
        const itemToRemove = currentInstance.products.find(p => p.productId === productId);
        if (itemToRemove) {
          removedItemName = itemToRemove.name || 'Unknown Item';
        }
        
        currentInstance.products = currentInstance.products.filter(p => p.productId !== productId);
        currentInstance.lastUpdatedAt = serverTimestamp();
        if (currentInstance.products.length === 0) {
          delete cartData[supermarketId][cartInstanceId];
          if (Object.keys(cartData[supermarketId]).length === 0) delete cartData[supermarketId];
        }
        const remainingSupermarketKeys = Object.keys(cartData).filter(k => k !== 'lastGlobalUpdate');
        if (remainingSupermarketKeys.length === 0) transaction.delete(cartRef);
        else transaction.update(cartRef, { ...cartData, lastGlobalUpdate: serverTimestamp() });
        
        // Store only the active cart instance data for notification
        const activeInstance = cartData[supermarketId] ? Object.values(cartData[supermarketId]).find(instance => 
          instance && instance.status === 'active'
        ) : null;
        updatedCartData = activeInstance ? { [cartInstanceId]: activeInstance } : {};
      });
      
      // Show notification for item removal only if there are still items in cart
      if (removedItemName && updatedCartData && Object.keys(updatedCartData).length > 0) {
        // Check if there are any active cart instances with products
        const hasActiveItems = Object.values(updatedCartData).some(instance => 
          instance && instance.status === 'active' && instance.products && instance.products.length > 0
        );
        
        if (hasActiveItems) {
          const supermarketName = supermarkets[supermarketId]?.display_name || 'Unknown Supermarket';
          showCartUpdateNotification(
            showNotification,
            navigate,
            'removed',
            removedItemName,
            updatedCartData,
            supermarketId,
            supermarketName,
            currentUser.uid
          );
        } else {
          // Cart is now empty, show a simple notification
          showNotification({
            type: 'success',
            title: 'Item Removed',
            message: `${removedItemName} has been removed from your cart.`,
            duration: 3000
          });
        }
      }
    } catch (error) {
      alert(`Failed to remove item. ${  error.message}`);
    }
  };

  const handleChangeQuantity = async (supermarketId, cartInstanceId, productId, newQuantity) => {
    logger.debug('[Carts] handleChangeQuantity called with:', { supermarketId, cartInstanceId, productId, newQuantity });
    
    if (!currentUser) {
      logger.warn('[Carts] No current user, cannot update quantity');
      return;
    }
    
    if (newQuantity < 0) {
      logger.warn('[Carts] Invalid quantity (negative):', newQuantity);
      return;
    }
    
    if (newQuantity === 0) {
      logger.debug('[Carts] Quantity is 0, removing item');
      handleRemoveItem(supermarketId, cartInstanceId, productId); 
      return;
    }
    
    const cartRef = doc(db, 'Carts', currentUser.uid);
    try {
      let updatedItemName = '';
      let updatedCartData = null;
      
      await runTransaction(db, async (transaction) => {
        const cartDoc = await transaction.get(cartRef);
        if (!cartDoc.exists()) {
          logger.warn('[Carts] Cart document does not exist');
          return;
        }
        
        const cartData = cartDoc.data();
        if (!cartData || !cartData[supermarketId] || !cartData[supermarketId][cartInstanceId]) {
          logger.warn('[Carts] Cart instance not found:', { supermarketId, cartInstanceId });
          return;
        }
        
        const currentInstance = cartData[supermarketId][cartInstanceId];
        if (currentInstance.status !== 'active') {
          logger.warn('[Carts] Cart instance is not active:', currentInstance.status);
          alert("Only items in an 'active' cart can be modified.");
          return;
        }
        
        const productIndex = currentInstance.products.findIndex(p => p.productId === productId);
        if (productIndex > -1) {
          logger.debug('[Carts] Updating product quantity from', currentInstance.products[productIndex].quantity, 'to', newQuantity);
          
          // Get the item name before updating
          updatedItemName = currentInstance.products[productIndex].name || 'Unknown Item';
          
          currentInstance.products[productIndex].quantity = newQuantity;
          currentInstance.lastUpdatedAt = serverTimestamp();
          transaction.update(cartRef, { 
              [`${supermarketId}.${cartInstanceId}`]: currentInstance, 
              lastGlobalUpdate: serverTimestamp() 
            });
            
          // Store only the active cart instance data for notification
          updatedCartData = { [cartInstanceId]: currentInstance };
        } else {
          logger.warn('[Carts] Product not found in cart:', productId);
        }
      });
      
      // Show notification for quantity update
      if (updatedItemName && updatedCartData) {
        const supermarketName = supermarkets[supermarketId]?.display_name || 'Unknown Supermarket';
        showCartUpdateNotification(
          showNotification,
          navigate,
          'updated',
          updatedItemName,
          updatedCartData,
          supermarketId,
          supermarketName,
          currentUser.uid
        );
      }
      
      logger.debug('[Carts] Quantity update completed successfully');
    } catch (error) {
      logger.error('[Carts] Failed to update quantity:', error);
      alert(`Failed to update quantity. ${error.message}`);
    }
  };

  const handleCancelPaymentAndResetCart = async (supermarketId, cartInstanceId, transactionId) => {
    if (!currentUser || !transactionId) {
        alert("User or Transaction ID missing for cancellation."); return;
    }
    setActionLoading(cartInstanceId);
    setActionError('');
    try {
        const cartRef = doc(db, 'Carts', currentUser.uid);
        const transactionRef = doc(db, 'Transactions', transactionId);
        await updateDoc(transactionRef, {
            status: 'payment_cancelled_by_user',
            updatedAt: serverTimestamp()
        });
        const cartInstanceUpdate = {};
        cartInstanceUpdate[`${supermarketId}.${cartInstanceId}.status`] = 'active';
        cartInstanceUpdate[`${supermarketId}.${cartInstanceId}.transactionId`] = deleteField();
        cartInstanceUpdate[`${supermarketId}.${cartInstanceId}.hasUnseenUpdate`] = false; // Clear flag explicitly
        cartInstanceUpdate[`${supermarketId}.${cartInstanceId}.lastUpdatedAt`] = serverTimestamp();
        cartInstanceUpdate.lastGlobalUpdate = serverTimestamp();
        await updateDoc(cartRef, cartInstanceUpdate);
        alert("Payment has been cancelled and the cart has been reset.");
    } catch (err) {
        setActionError(`Failed to cancel payment. Error: ${  err.message}`);
        alert(`Failed to cancel payment: ${  err.message}`);
    }
    setActionLoading(null);
  };

  const handleProceedToCheckout = (supermarketId, cartInstanceId) => navigate(`/checkout/${supermarketId}/${cartInstanceId}`);
  const handleViewOrderStatus = (transactionId) => {
    navigate(`/order-status/${transactionId}`);
  };

  // Function to confirm pickup or delivery
  const handleConfirmPickupOrDelivery = async (supermarketId, instanceId, instanceData) => {
    if (!currentUser) {
      alert('Please log in to confirm pickup/delivery.');
      return;
    }

    // Check if this is the user's own order
    if (instanceData.userId && instanceData.userId !== currentUser.uid) {
      alert('You can only confirm pickup/delivery for your own orders.');
      return;
    }

    const actionText = resolveCartDeliveryOption(instanceData) === 'delivery' ? 'delivery' : 'pickup';
    if (!window.confirm(`Are you sure you want to confirm ${actionText} for this order? This action cannot be undone.`)) {
      return;
    }

    setConfirmingOrder(instanceId);
    setActionError('');

    try {
      // Update the cart instance status to completed
      const cartRef = doc(db, 'Carts', currentUser.uid);
      const cartUpdatePayload = {
        [`${supermarketId}.${instanceId}.status`]: 'completed',
        [`${supermarketId}.${instanceId}.hasUnseenUpdate`]: true,
        [`${supermarketId}.${instanceId}.lastUpdatedAt`]: Timestamp.fromDate(new Date()),
        [`${supermarketId}.${instanceId}.completedAt`]: Timestamp.fromDate(new Date()),
        lastGlobalUpdate: Timestamp.fromDate(new Date())
      };

      // If there's a transaction ID, also update the transaction
      if (instanceData.transactionId) {
        const transactionRef = doc(db, 'Transactions', instanceData.transactionId);
        const transactionUpdatePayload = {
          status: 'completed',
          updatedAt: Timestamp.fromDate(new Date()),
          completedAt: Timestamp.fromDate(new Date())
          // Note: We do NOT set pickedUpAt here to indicate this was user confirmation, not employee confirmation
        };

        await updateDoc(transactionRef, transactionUpdatePayload);
      } else {
        logger.warn(`[Carts.js] No transaction ID found for order ${instanceId}`);
      }

      // Update the cart instance
      await updateDoc(cartRef, cartUpdatePayload);

      // Show success message
      setToastMessage(`Order ${actionText} confirmed successfully!`);
      setShowToast(true);

    } catch (error) {
      logger.error('Error confirming pickup/delivery:', error);
      setActionError(`Failed to confirm ${actionText}. ${error.message}`);
    } finally {
      setConfirmingOrder(null);
    }
  };

  // Function to fetch product details and images
  const fetchProductDetails = useCallback(async (productId) => {
    if (productDetails[productId]) return; // Already fetched
    
    try {
      const productDoc = await getDoc(doc(db, 'Products', productId));
      if (productDoc.exists()) {
        const productData = productDoc.data();
        setProductDetails(prev => ({ ...prev, [productId]: productData }));
        
        // Fetch product image if available
        if (productData.media && productData.media.length > 0 && productData.media[0].url) {
          try {
            const storage = getStorage();
            const imageRef = ref(storage, productData.media[0].url);
            const imageUrl = await getDownloadURL(imageRef);
            setProductImages(prev => ({ ...prev, [productId]: imageUrl }));
          } catch (imageError) {
            logger.warn(`Failed to load image for product ${productId}:`, imageError);
          }
        }
      }
    } catch (error) {
      logger.error(`Failed to fetch product details for ${productId}:`, error);
    }
  }, [productDetails]);

  // Function to fetch inventory details for deals
  const fetchInventoryDetails = useCallback(async (productId, supermarketId) => {
    const key = `${productId}_${supermarketId}`;
    if (inventoryDetails[key]) return; // Already fetched
    
    try {
      const inventoryRef = collection(db, 'Inventory');
      const inventoryQuery = query(
        inventoryRef,
        where('product_id', '==', productId),
        where('supermarket_id', '==', supermarketId),
        limit(1)
      );
      
      const inventorySnapshot = await getDocs(inventoryQuery);
      if (!inventorySnapshot.empty) {
        const inventoryData = inventorySnapshot.docs[0].data();
        setInventoryDetails(prev => ({ ...prev, [key]: inventoryData }));
      }
    } catch (error) {
      logger.error(`Failed to fetch inventory details for ${productId}:`, error);
    }
  }, [inventoryDetails]);

  // Effect to fetch product details when cart changes
  useEffect(() => {
    if (!cart) return;
    
    const productIds = new Set();
    const productSupermarketPairs = new Set();
    
    Object.keys(cart).forEach(supermarketId => {
      if (supermarketId === 'lastGlobalUpdate' || typeof cart[supermarketId] !== 'object') return;
      const supermarketInstances = cart[supermarketId];
      Object.keys(supermarketInstances).forEach(instanceId => {
        const instanceData = supermarketInstances[instanceId];
        if (instanceData && instanceData.products) {
          instanceData.products.forEach(product => {
            if (product.productId) {
              productIds.add(product.productId);
              productSupermarketPairs.add(`${product.productId}_${supermarketId}`);
            }
          });
        }
      });
    });
    
    // Fetch details for all products
    productIds.forEach(productId => {
      fetchProductDetails(productId);
    });
    
    // Fetch inventory details for all product-supermarket pairs
    productSupermarketPairs.forEach(pair => {
      const [productId, supermarketId] = pair.split('_');
      fetchInventoryDetails(productId, supermarketId);
    });
  }, [cart, fetchProductDetails, fetchInventoryDetails]);

  if (loading) return <Layout><Container className="mt-4 text-center"><p>Loading cart...</p></Container></Layout>;
  if (!currentUser) {
    return (
      <Layout>
        <Container className="mt-4 px-3">
          <Row className="align-items-center mb-1">
            <Col xs="auto">
              <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
                &larr; Go Back
              </Button>
            </Col>
            <Col>
              <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
          </Row>
          <div className="text-center mt-5 mb-5">
            <h2 className="mb-4">Your Cart</h2>
            <div className="mb-4">
              <i className="fas fa-shopping-cart fa-4x text-muted mb-3"></i>
              <p className="text-muted fs-5">Please log in to view your cart and start shopping.</p>
            </div>
            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center mb-5">
              <Button 
                variant="primary" 
                size="lg" 
                onClick={() => navigate(`/login?path=${  encodeURIComponent(window.location.pathname)}`)}
                className="px-4"
              >
                <i className="fas fa-sign-in-alt me-2"></i>
                Log In
              </Button>
              <Button 
                variant="outline-secondary" 
                size="lg" 
                onClick={() => navigate('/')}
                className="px-4"
              >
                <i className="fas fa-home me-2"></i>
                Browse Products
              </Button>
            </div>
          </div>
        </Container>
      </Layout>
    );
  }
  
  const hasAnyDisplayableCartContent = cart && Object.keys(cart).some(smId => 
    smId !== 'lastGlobalUpdate' && typeof cart[smId] === 'object' && cart[smId] !== null &&
    Object.keys(cart[smId]).some(instId => {
        const instance = cart[smId][instId];
        return instance && ( (instance.products && instance.products.length > 0) || (instance.status && instance.status !== 'active') );
    })
  );

  if (!cart || !hasAnyDisplayableCartContent) {
    return (
        <Layout>
            <NoIndexHelmet title="Your Cart | Pack My Cart" path="/carts" />
            <Container className="mt-4 px-3">
                <Row className="align-items-center mb-1">
                    <Col xs="auto"><Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">&larr; Go Back</Button></Col>
                    <Col><BreadcrumbNavigation items={breadcrumbItems} /></Col>
                </Row>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mt-3 gap-2">
                    <h2 className="mb-0">Your Cart</h2>
                    <Link to="/order-history" className="btn btn-outline-primary">View Order History</Link>
                </div>
                <p className="text-center text-md-start mb-5">Your cart is currently empty, or all items have been processed into orders.</p>
            </Container>
        </Layout>
    );
  }

  let totalCartValueAllActive = 0;
  Object.keys(cart).forEach(supermarketIdKey => {
    if (supermarketIdKey === 'lastGlobalUpdate' || typeof cart[supermarketIdKey] !== 'object') return;
    const supermarketInstances = cart[supermarketIdKey];
    Object.keys(supermarketInstances).forEach(instanceIdKey => {
        const instanceData = supermarketInstances[instanceIdKey];
        if (instanceData && instanceData.products && instanceData.status === 'active') {
            instanceData.products.forEach(product => { 
              const dealCalculation = calculateDealPriceForProduct(product.productId, product.quantity, product.price || 0, supermarketIdKey);
              totalCartValueAllActive += dealCalculation.price;
            });
        }
    });
  });

  // Helper for status filter buttons
  const statusOptions = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'awaiting_packing', label: 'Awaiting Packing' },
    { key: 'packed_awaiting_pickup', label: 'Ready for Pickup' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' }
  ];

  // Get all supermarket IDs and names for the dropdown
  const supermarketOptions = Object.keys(cart || {})
    .filter(id => id !== 'lastGlobalUpdate' && id !== 'lastUpdatedAt' && typeof cart[id] === 'object') // filter out lastUpdatedAt
    .map(id => {
      // Try to get a display name from the first instance, fallback to ID
      const firstInstance = cart[id] && Object.values(cart[id])[0];
      const supermarketData = supermarkets[id];
      return {
        id,
        name: firstInstance && firstInstance.supermarketName ? firstInstance.supermarketName : id,
        display_name: supermarketData?.display_name || firstInstance?.supermarketName || id,
        address: supermarketData?.address || '',
        media: supermarketData?.media || null
      };
    });

  return (
    <Layout>
      <NoIndexHelmet title="Your Cart | Pack My Cart" path="/carts" />
      <Container className="mt-4 px-3" data-testid="cart-container">
        <Row className="align-items-center mb-1">
            <Col xs="auto"><Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">&larr; Go Back</Button></Col>
            <Col><BreadcrumbNavigation items={breadcrumbItems} /></Col>
        </Row>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mt-3 gap-2">
          <h2 className="mb-0">Your Cart</h2>
          {currentUser && <Link to="/order-history" className="btn btn-outline-primary">View Order History</Link>}
        </div>
        
        {/* FCM Notification Permission for logged-in users - skip on iOS Safari (limited support, can cause blank screen) */}
        {currentUser && !isIOSOrMobileSafari() && (
          <div className="notification-permission-wrapper">
            <NotificationPermission />
          </div>
        )}
        
        {/* Notification Message for Unseen Updates */}
        {showNotificationMessage && (
          <Alert variant="success" className="mb-3" onClose={() => setShowNotificationMessage(false)} dismissible>
            <div className="d-flex align-items-center">
              <i className="fas fa-shopping-cart me-2"></i>
              <div>
                <strong>New Items Added to Your Cart!</strong>
                <br />
                <small>
                  You have new products in your active cart. Look for items with <span className="badge bg-success">Updated</span> badges and green highlighting below. 
                  You can modify quantities or remove items from active carts.
                </small>
              </div>
            </div>
          </Alert>
        )}
        
        {/* Toast for newly added products */}
        <ToastContainer position="top-end" className="mt-3">
          <Toast show={showToast} onClose={() => setShowToast(false)} delay={5000} autohide>
            <Toast.Header>
              <i className="fas fa-info-circle me-2 text-primary"></i>
              <strong className="me-auto">New Items Added</strong>
            </Toast.Header>
            <Toast.Body>{toastMessage}</Toast.Body>
          </Toast>
        </ToastContainer>

        {actionError && <Alert variant="danger">{actionError}</Alert>}

        {/* Enhanced Filter by Supermarket */}
        <div className="mb-3">
          <SupermarketDropdown
            supermarketOptions={supermarketOptions}
            supermarketFilter={supermarketFilter}
            onSupermarketFilterChange={setSupermarketFilter}
          />
        </div>

        {Object.keys(cart).map(supermarketId => {
          // Skip meta/system keys like lastGlobalUpdate and lastUpdatedAt
          if (
            supermarketId === 'lastGlobalUpdate' ||
            supermarketId === 'lastUpdatedAt' ||
            typeof cart[supermarketId] !== 'object'
          ) return null;
          if (supermarketFilter && supermarketId !== supermarketFilter) return null; // <-- filter here
          const supermarketCartInstances = cart[supermarketId];

          // --- Filter UI ---
          const filterValue = cartFilters[supermarketId] || 'active';
          
          // Check if any cart instances in this supermarket have unseen updates
          const supermarketHasUpdates = Object.keys(supermarketCartInstances).some(instanceId => {
            const instanceData = supermarketCartInstances[instanceId];
            return instanceData && instanceData.hasUnseenUpdate === true;
          });

          // Check which statuses have unseen updates for highlighting filter buttons
          const statusWithUpdates = new Set();
          const statusCounts = {
            all: 0,
            active: 0,
            awaiting_packing: 0,
            packed_awaiting_pickup: 0,
            completed: 0,
            cancelled: 0
          };
          
          Object.keys(supermarketCartInstances).forEach(instanceId => {
            const instanceData = supermarketCartInstances[instanceId];
            if (instanceData && instanceData.hasUnseenUpdate === true) {
              // Map status to filter key
              let statusKey = 'all'; // default
              if (instanceData.status === 'active') statusKey = 'active';
              else if (instanceData.status === 'awaiting_packing') statusKey = 'awaiting_packing';
              else if (instanceData.status === 'packed_awaiting_pickup') statusKey = 'packed_awaiting_pickup';
              else if (instanceData.status === 'completed') statusKey = 'completed';
              else if (['cancelled_by_employee', 'payment_cancelled_by_user'].includes(instanceData.status)) statusKey = 'cancelled';
              statusWithUpdates.add(statusKey);
            }
            
            // Count instances for each status
            if (instanceData && ((instanceData.products && instanceData.products.length > 0) || (instanceData.status && instanceData.status !== 'active'))) {
              statusCounts.all++;
              
              if (instanceData.status === 'active') statusCounts.active++;
              else if (instanceData.status === 'awaiting_packing') statusCounts.awaiting_packing++;
              else if (instanceData.status === 'packed_awaiting_pickup') statusCounts.packed_awaiting_pickup++;
              else if (instanceData.status === 'completed') statusCounts.completed++;
              else if (['cancelled_by_employee', 'payment_cancelled_by_user'].includes(instanceData.status)) statusCounts.cancelled++;
            }
          });
          
          const displayableInstancesForSupermarket = Object.keys(supermarketCartInstances).filter(instanceId => {
            const instanceData = supermarketCartInstances[instanceId];
            if (!instanceData) return false;
            let matchesStatus = false;
            if (filterValue === 'all') matchesStatus = true;
            else if (filterValue === 'cancelled') {
              matchesStatus = ['cancelled_by_employee', 'payment_cancelled_by_user'].includes(instanceData.status);
            } else {
              matchesStatus = instanceData.status === filterValue;
            }
            return ((instanceData.products && instanceData.products.length > 0) || (instanceData.status && instanceData.status !== 'active')) && matchesStatus;
          });

          // Apply pagination for all filters
          const currentPage = paginationState[`${supermarketId}_${filterValue}`] || 1;
          const startIndex = (currentPage - 1) * ordersPerPage;
          const endIndex = startIndex + ordersPerPage;
          const paginatedInstances = displayableInstancesForSupermarket.slice(startIndex, endIndex);

          return (
            <div key={supermarketId} className={`mb-4 ${supermarketHasUpdates ? 'border border-success border-2 rounded p-3 bg-success bg-opacity-5' : ''}`}>
              {Object.keys(supermarketCartInstances).length > 0 && 
                <div className={`d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-2 gap-2 ${supermarketHasUpdates ? 'p-2 rounded bg-success bg-opacity-10' : ''}`}>
                  <div className="d-flex align-items-center gap-2">
                    <h3 className={`mb-0 ${supermarketHasUpdates ? 'text-success fw-bold' : ''}`}>
                      {supermarketCartInstances[Object.keys(supermarketCartInstances)[0]].supermarketName || supermarketId}
                    </h3>
                    {supermarketHasUpdates && (
                      <Badge bg="success" className="animate-pulse">
                        <i className="fas fa-bell me-1"></i>
                        Has Updates
                      </Badge>
                    )}
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {statusOptions.map(opt => {
                      const hasUpdatesForThisStatus = statusWithUpdates.has(opt.key);
                      const isSelected = filterValue === opt.key;
                      const count = statusCounts[opt.key] || 0;
                      
                      let buttonVariant = isSelected ? "primary" : "outline-primary";
                      let buttonClass = "mb-1";
                      
                      // Add highlighting for buttons with updates
                      if (hasUpdatesForThisStatus) {
                        buttonVariant = isSelected ? "success" : "outline-success";
                        buttonClass += " animate-pulse";
                      }
                      
                      return (
                        <Button
                          key={opt.key}
                          variant={buttonVariant}
                          onClick={() => {
                            setCartFilters(f => ({ ...f, [supermarketId]: opt.key }));
                            // Reset pagination when switching filters
                            setPaginationState(prev => ({
                              ...prev,
                              [`${supermarketId}_${opt.key}`]: 1
                            }));
                          }}
                          size="sm"
                          className={buttonClass}
                        >
                          {opt.label}
                          {count > 0 && (
                            <Badge 
                              bg={isSelected ? "light" : "warning"} 
                              text={isSelected ? "dark" : "dark"}
                              className="ms-1"
                            >
                              {count}
                            </Badge>
                          )}
                          {hasUpdatesForThisStatus && (
                            <Badge bg="warning" className="ms-1 animate-pulse">
                              <i className="fas fa-bell"></i>
                            </Badge>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              }
              {paginatedInstances.length === 0 ? (
                <Card className="mb-3">
                  <Card.Body>
                    <p className="mb-0 text-muted">No carts with this status.</p>
                  </Card.Body>
                </Card>
              ) : (
                paginatedInstances.map(instanceId => {
                  const instanceData = supermarketCartInstances[instanceId];
                  if (!instanceData) return null; 
                  let instanceTotal = 0;
                  if(instanceData.products) {
                    instanceData.products.forEach(product => { 
                      const dealCalculation = calculateDealPriceForProduct(product.productId, product.quantity, product.price || 0, supermarketId);
                      instanceTotal += dealCalculation.price;
                    });
                  }
                  const instanceTaxDetailsById = (instanceData.products || []).reduce((result, product) => {
                    result[product.productId] = inventoryDetails[`${product.productId}_${supermarketId}`] || {};
                    return result;
                  }, {});
                  const instanceTaxSummary = calculateCartGctBreakdown({
                    cartItems: instanceData.products || [],
                    inventoryDetails: instanceTaxDetailsById,
                    supermarket: supermarkets[supermarketId] || {},
                    getLineTotal: (product) =>
                      calculateDealPriceForProduct(product.productId, product.quantity, product.price || 0, supermarketId).price,
                  });
                  const isModifiable = instanceData.status === 'active';
                  logger.debug('[Carts] Cart instance status:', instanceData.status, 'isModifiable:', isModifiable);
                  
                  // Only allow editing for active carts
                  const isEditable = instanceData.status === 'active';
                  
                  // Debug: Log all cart instance data
                  logger.debug('[Carts] Full cart instance data:', {
                    instanceId,
                    status: instanceData.status,
                    isModifiable,
                    isEditable,
                    products: instanceData.products?.length || 0
                  });
                  const deliveryOption = resolveCartDeliveryOption(instanceData);
                  const { statusText, statusBadgeClass } = getCartStatusBadge(instanceData.status, {
                    deliveryOption,
                  });
                  
                  // Only highlight cart instances that actually have unseen updates
                  const hasUpdate = instanceData.hasUnseenUpdate === true;
                  
                  const cardHighlightClass = hasUpdate ? 'border-success border-3 shadow-lg' : 'shadow-sm';
                  const cardBorderColorClass = !isModifiable && instanceData.status !== 'completed' ? 'border-info' : isModifiable ? 'border-primary' : 'border-secondary';

                  return (
                    <Card className={`mb-3 ${cardBorderColorClass} ${cardHighlightClass}`} key={instanceId}>
                      <Card.Header className={`d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 ${
                        hasUpdate ? 'bg-success bg-opacity-25' : ''
                      }`}>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <span>Cart ID: {instanceId.replace('cart_instance_','')}</span>
                          <Badge className={statusBadgeClass}>{statusText}</Badge>
                          {hasUpdate && (
                            <Badge bg="success" className="animate-pulse">
                              <i className="fas fa-bell me-1"></i>
                              Updated
                            </Badge>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {instanceData.transactionId && instanceData.status !== 'active' && instanceData.status !== 'awaiting_payment_confirmation' && (
                              <Button variant="outline-info" size="sm" onClick={() => handleViewOrderStatus(instanceData.transactionId)}>View Order Status</Button>
                          )}
                          {instanceData.products && instanceData.products.length > 0 && StatusHelpers.isSaveableAsList(instanceData.status) && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setSaveListContext({
                                  supermarketId,
                                  supermarketName: instanceData.supermarketName || supermarkets[supermarketId]?.display_name || supermarketId,
                                  products: instanceData.products
                                })}
                              >
                                <i className="fas fa-list me-1"></i>
                                Save as List
                              </Button>
                          )}
                          {isModifiable && instanceData.products && instanceData.products.length > 0 && (
                              <Button
                                variant="success"
                                size="lg"
                                onClick={() => handleProceedToCheckout(supermarketId, instanceId)}
                                className="fw-bold shadow-sm"
                                style={{
                                  backgroundColor: '#28a745',
                                  borderColor: '#28a745',
                                  minWidth: '180px'
                                }}
                              >
                                <i className="fas fa-shopping-cart me-2"></i>
                                Checkout Cart
                              </Button>
                          )}
                          {instanceData.status === 'awaiting_payment_confirmation' && instanceData.transactionId && (
                               <Button variant="danger" size="sm" onClick={() => handleCancelPaymentAndResetCart(supermarketId, instanceId, instanceData.transactionId)} disabled={actionLoading === instanceId}>
                                  {actionLoading === instanceId ? 'Cancelling...' : 'Cancel Payment'}
                              </Button>
                          )}
                          {shouldShowPickupVerificationQr(instanceData) && (
                            <Button
                              variant="outline-success"
                              size="lg"
                              onClick={() => setPickupQrCartInstanceId(instanceId)}
                              className="fw-bold shadow-sm"
                              style={{ minWidth: '180px' }}
                            >
                              <i className="fas fa-qrcode me-2"></i>
                              View QR Code
                            </Button>
                          )}
                          {instanceData.status === 'packed_awaiting_pickup' && (
                            <Button 
                              variant="success" 
                              size="lg" 
                              onClick={() => handleConfirmPickupOrDelivery(supermarketId, instanceId, instanceData)} 
                              disabled={confirmingOrder === instanceId}
                              className="fw-bold shadow-sm"
                              style={{ 
                                backgroundColor: '#28a745', 
                                borderColor: '#28a745',
                                minWidth: '180px'
                              }}
                            >
                              {confirmingOrder === instanceId ? (
                                <>
                                  <Spinner as="span" size="sm" className="me-2"/> 
                                  Confirming...
                                </>
                              ) : (
                                <>
                                  <i className="fas fa-check-circle me-2"></i>
                                  {resolveCartDeliveryOption(instanceData) === 'delivery' ? 'Confirm Delivery' : 'Confirm Pickup'}
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </Card.Header>
                      {instanceData.products && instanceData.products.length > 0 ? (
                        <ListGroup variant="flush">
                          {instanceData.products.map(product => {
                            const productDetail = productDetails[product.productId];
                            const productImage = productImages[product.productId];
                            const dealCalculation = calculateDealPriceForProduct(product.productId, product.quantity, product.price, supermarketId);
                            
                            return (
                              <ListGroup.Item 
                                key={product.productId} 
                                className={`d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between py-3 px-3 compact-cart-item ${
                                  hasUpdate ? 'bg-success bg-opacity-10 border-success border-start border-4' : ''
                                }`}
                              >
                                <div className="d-flex align-items-center flex-grow-1 w-100">
                                  {/* Product Image */}
                                  <div className="me-3" style={{ width: '60px', height: '60px', flexShrink: 0, position: 'relative' }}>
                                    {productImage ? (
                                      <Image 
                                        src={productImage} 
                                        alt={product.name || 'Product'} 
                                        className="img-fluid rounded"
                                        style={{ 
                                          width: '100%', 
                                          height: '100%', 
                                          objectFit: 'cover'
                                        }}
                                        onError={(e) => {
                                          logger.warn(`Image failed to load for product ${product.productId}`);
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <div 
                                        className="d-flex align-items-center justify-content-center bg-light rounded"
                                        style={{ 
                                          width: '100%', 
                                          height: '100%', 
                                          fontSize: '0.8rem',
                                          color: '#6c757d'
                                        }}
                                      >
                                        No Image
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Product Details */}
                                  <div className="flex-grow-1">
                                    <div className="fw-bold small mb-1">
                                      {product.name || productDetail?.display_name || 'Product'}
                                    </div>
                                    <div className="text-muted small">
                                      ${product.price !== undefined ? product.price.toFixed(2) : 'N/A'} each
                                    </div>
                                    {productDetail?.description && (
                                      <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                        {productDetail.description.length > 50 
                                          ? `${productDetail.description.substring(0, 50)}...` 
                                          : productDetail.description}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Quantity and Actions */}
                                <div className="d-flex align-items-center flex-shrink-0 mt-2 mt-md-0">
                                  <div className="d-flex align-items-center gap-2">
                                    {(() => {
                                      const currentQuantity = getCurrentQuantity(product.productId, instanceId, supermarketId);
                                      const displayValue = currentQuantity !== undefined ? currentQuantity : product.quantity;
                                      return (
                                        <input 
                                          type="number" 
                                          value={displayValue}
                                      data-testid={`quantity-input-${product.productId}`}
                                      onChange={(e) => {
                                        // Allow typing in the input field for immediate UI feedback
                                        // The actual database update happens in onBlur
                                        const newValue = e.target.value;
                                        const localKey = `${supermarketId}_${instanceId}_${product.productId}`;
                                        
                                        logger.debug('[Carts] onChange triggered, new value:', newValue);
                                        
                                        // Update local state to allow typing
                                        setLocalQuantities(prev => ({
                                          ...prev,
                                          [localKey]: newValue
                                        }));
                                      }}
                                      onFocus={(e) => {
                                        // Store the original value when focusing
                                        e.target.dataset.originalValue = e.target.value;
                                        logger.debug('[Carts] Input focused, original value:', e.target.value);
                                        logger.debug('[Carts] Input element:', e.target);
                                        logger.debug('[Carts] Input disabled state:', e.target.disabled);
                                      }}
                                      onBlur={(e) => {
                                        const newValue = parseInt(e.target.value);
                                        const originalValue = parseInt(e.target.dataset.originalValue);
                                        const localKey = `${supermarketId}_${instanceId}_${product.productId}`;
                                        
                                        logger.debug('[Carts] Input blurred, new value:', newValue, 'original value:', originalValue);
                                        
                                        // Clear local state
                                        setLocalQuantities(prev => {
                                          const newState = { ...prev };
                                          delete newState[localKey];
                                          return newState;
                                        });
                                        
                                        // Validate the new value
                                        if (isNaN(newValue) || newValue < 0) {
                                          logger.warn('[Carts] Invalid quantity value, reverting to original');
                                          return;
                                        }
                                        
                                        // Only update if the value has actually changed
                                        if (newValue !== originalValue) {
                                          logger.debug('[Carts] Quantity changed, updating from', originalValue, 'to', newValue);
                                          handleChangeQuantity(supermarketId, instanceId, product.productId, newValue);
                                        } else {
                                          logger.debug('[Carts] No quantity change detected');
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        const localKey = `${supermarketId}_${instanceId}_${product.productId}`;
                                        
                                        // Handle Enter key to trigger blur event
                                        if (e.key === 'Enter') {
                                          e.target.blur();
                                        }
                                        // Handle Escape key to revert to original value
                                        if (e.key === 'Escape') {
                                          // Clear local state to revert to original value
                                          setLocalQuantities(prev => {
                                            const newState = { ...prev };
                                            delete newState[localKey];
                                            return newState;
                                          });
                                          e.target.blur();
                                        }
                                      }}
                                      className="form-control form-control-sm text-center" 
                                      style={{
                                        width: '60px', 
                                        minWidth: '60px', 
                                        border: isEditable ? '2px solid #007bff' : '1px solid #dee2e6',
                                        backgroundColor: isEditable ? '#fff' : '#f8f9fa'
                                      }}
                                      placeholder="Qty" 
                                      min="0" 
                                      disabled={!isEditable}
                                      data-debug-modifiable={isModifiable}
                                      data-debug-editable={isEditable}
                                      data-debug-status={instanceData.status}
                                    />
                                      );
                                    })()}
                                    <div className="d-flex flex-column align-items-end">
                                      {dealCalculation.dealApplied ? (
                                        <>
                                          <span className="small fw-semibold text-success">${dealCalculation.price.toFixed(2)}</span>
                                          <small className="text-muted text-decoration-line-through">${(product.price * product.quantity).toFixed(2)}</small>
                                          <small className="text-success">Save ${dealCalculation.savings.toFixed(2)}</small>
                                        </>
                                      ) : (
                                        <span className="small fw-semibold">${(product.price * product.quantity).toFixed(2)}</span>
                                      )}
                                    </div>
                                    <button 
                                      className="btn btn-danger btn-sm px-2 py-1" 
                                      onClick={() => handleRemoveItem(supermarketId, instanceId, product.productId)} 
                                      disabled={!isModifiable}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              </ListGroup.Item>
                            );
                          })}
                        </ListGroup>
                      ) : (<Card.Body className="py-2 px-2"><p className="mb-0 small">This cart instance {instanceData.status !== 'active' ? `(Status: ${statusText})` : 'is empty'}.</p></Card.Body>)}
                      <Card.Footer>
                        <strong>Cart Instance Total: ${instanceTotal.toFixed(2)}</strong>{instanceData.transactionId && <small className="ms-2 text-muted">Order ID: {instanceData.transactionId}</small>}
                        {instanceTotal > 0 && (
                          <GctSummaryBreakdown taxSummary={instanceTaxSummary} className="mt-2 pt-2 border-top" />
                        )}
                      </Card.Footer>
                    </Card>
                  );
                })
              )}

              {/* Pagination for all filters */}
              {displayableInstancesForSupermarket.length > ordersPerPage && (
                <div className="mt-4">
                  {/* Pagination info */}
                  <div className="text-center mb-3">
                    <div className="d-flex justify-content-center align-items-center gap-3">
                      <span className="badge bg-primary fs-6 px-3 py-2">
                        <i className="fas fa-list me-1"></i>
                        Showing {((currentPage - 1) * ordersPerPage) + 1} to {Math.min(currentPage * ordersPerPage, displayableInstancesForSupermarket.length)} of {displayableInstancesForSupermarket.length} {filterValue.replace('_', ' ')} orders
                      </span>
                    </div>
                  </div>

                  {/* Enhanced pagination controls */}
                  <div className="d-flex justify-content-center">
                    <nav aria-label={`${filterValue} orders pagination`}>
                      <ul className="pagination pagination-lg mb-0">
                        {/* First page button */}
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link border-0 px-3 py-2 mx-1 rounded-3 shadow-sm" 
                            onClick={() => setPaginationState(prev => ({
                              ...prev,
                              [`${supermarketId}_${filterValue}`]: 1
                            }))}
                            disabled={currentPage === 1}
                            style={{
                              backgroundColor: currentPage === 1 ? '#f8f9fa' : '#ffffff',
                              color: currentPage === 1 ? '#6c757d' : '#0d6efd',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <i className="fas fa-angle-double-left"></i>
                          </button>
                        </li>

                        {/* Previous page button */}
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link border-0 px-3 py-2 mx-1 rounded-3 shadow-sm" 
                            onClick={() => setPaginationState(prev => ({
                              ...prev,
                              [`${supermarketId}_${filterValue}`]: Math.max(1, currentPage - 1)
                            }))}
                            disabled={currentPage === 1}
                            style={{
                              backgroundColor: currentPage === 1 ? '#f8f9fa' : '#ffffff',
                              color: currentPage === 1 ? '#6c757d' : '#0d6efd',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <i className="fas fa-angle-left"></i>
                          </button>
                        </li>
                        
                        {/* Page numbers */}
                        {(() => {
                          const totalPages = Math.ceil(displayableInstancesForSupermarket.length / ordersPerPage);
                          const pages = [];
                          
                          // Show first page
                          if (currentPage > 1) {
                            pages.push(
                              <li key="first" className="page-item">
                                <button 
                                  className="page-link border-0 px-3 py-2 mx-1 rounded-3 shadow-sm" 
                                  onClick={() => setPaginationState(prev => ({
                                    ...prev,
                                    [`${supermarketId}_${filterValue}`]: 1
                                  }))}
                                  style={{
                                    backgroundColor: '#ffffff',
                                    color: '#0d6efd',
                                    border: '1px solid #dee2e6'
                                  }}
                                >
                                  1
                                </button>
                              </li>
                            );
                          }
                          
                          // Show ellipsis if needed
                          if (currentPage > 3) {
                            pages.push(
                              <li key="ellipsis1" className="page-item disabled">
                                <span 
                                  className="page-link border-0 px-3 py-2 mx-1 rounded-3"
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    color: '#6c757d',
                                    border: '1px solid #dee2e6'
                                  }}
                                >
                                  <i className="fas fa-ellipsis-h"></i>
                                </span>
                              </li>
                            );
                          }
                          
                          // Show current page and neighbors
                          for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                            if (i > 1 && i < totalPages) {
                              pages.push(
                                <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
                                  <button 
                                    className="page-link border-0 px-3 py-2 mx-1 rounded-3 shadow-sm" 
                                    onClick={() => setPaginationState(prev => ({
                                      ...prev,
                                      [`${supermarketId}_${filterValue}`]: i
                                    }))}
                                    style={{
                                      backgroundColor: i === currentPage ? '#0d6efd' : '#ffffff',
                                      color: i === currentPage ? '#ffffff' : '#0d6efd',
                                      border: '1px solid #dee2e6',
                                      fontWeight: i === currentPage ? 'bold' : 'normal'
                                    }}
                                  >
                                    {i}
                                  </button>
                                </li>
                              );
                            }
                          }
                          
                          // Show ellipsis if needed
                          if (currentPage < totalPages - 2) {
                            pages.push(
                              <li key="ellipsis2" className="page-item disabled">
                                <span 
                                  className="page-link border-0 px-3 py-2 mx-1 rounded-3"
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    color: '#6c757d',
                                    border: '1px solid #dee2e6'
                                  }}
                                >
                                  <i className="fas fa-ellipsis-h"></i>
                                </span>
                              </li>
                            );
                          }
                          
                          // Show last page
                          if (currentPage < totalPages) {
                            pages.push(
                              <li key="last" className="page-item">
                                <button 
                                  className="page-link border-0 px-3 py-2 mx-1 rounded-3 shadow-sm" 
                                  onClick={() => setPaginationState(prev => ({
                                    ...prev,
                                    [`${supermarketId}_${filterValue}`]: totalPages
                                  }))}
                                  style={{
                                    backgroundColor: '#ffffff',
                                    color: '#0d6efd',
                                    border: '1px solid #dee2e6'
                                  }}
                                >
                                  {totalPages}
                                </button>
                              </li>
                            );
                          }
                          
                          return pages;
                        })()}
                        
                        {/* Next page button */}
                        <li className={`page-item ${currentPage >= Math.ceil(displayableInstancesForSupermarket.length / ordersPerPage) ? 'disabled' : ''}`}>
                          <button 
                            className="page-link border-0 px-3 py-2 mx-1 rounded-3 shadow-sm" 
                            onClick={() => setPaginationState(prev => ({
                              ...prev,
                              [`${supermarketId}_${filterValue}`]: Math.min(Math.ceil(displayableInstancesForSupermarket.length / ordersPerPage), currentPage + 1)
                            }))}
                            disabled={currentPage >= Math.ceil(displayableInstancesForSupermarket.length / ordersPerPage)}
                            style={{
                              backgroundColor: currentPage >= Math.ceil(displayableInstancesForSupermarket.length / ordersPerPage) ? '#f8f9fa' : '#ffffff',
                              color: currentPage >= Math.ceil(displayableInstancesForSupermarket.length / ordersPerPage) ? '#6c757d' : '#0d6efd',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <i className="fas fa-angle-right"></i>
                          </button>
                        </li>

                        {/* Last page button */}
                        <li className={`page-item ${currentPage >= Math.ceil(displayableInstancesForSupermarket.length / ordersPerPage) ? 'disabled' : ''}`}>
                          <button 
                            className="page-link border-0 px-3 py-2 mx-1 rounded-3 shadow-sm" 
                            onClick={() => setPaginationState(prev => ({
                              ...prev,
                              [`${supermarketId}_${filterValue}`]: Math.ceil(displayableInstancesForSupermarket.length / ordersPerPage)
                            }))}
                            disabled={currentPage >= Math.ceil(displayableInstancesForSupermarket.length / ordersPerPage)}
                            style={{
                              backgroundColor: currentPage >= Math.ceil(displayableInstancesForSupermarket.length / ordersPerPage) ? '#f8f9fa' : '#ffffff',
                              color: currentPage >= Math.ceil(displayableInstancesForSupermarket.length / ordersPerPage) ? '#6c757d' : '#0d6efd',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <i className="fas fa-angle-double-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>

                  {/* Quick navigation info */}
                  <div className="text-center mt-3">
                    <small className="text-muted">
                      <i className="fas fa-info-circle me-1"></i>
                      Use the navigation buttons above to browse through your {filterValue.replace('_', ' ')} orders
                    </small>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {totalCartValueAllActive > 0 && (
          <div className="mt-4 alert alert-info">
            <h4 className="mb-0 text-center text-md-start">Total Value of All Active Carts: ${totalCartValueAllActive.toFixed(2)}</h4>
          </div>
        )}

        {currentUser && (
          <ShoppingListFormModal
            show={saveListContext !== null}
            mode="create"
            userId={currentUser.uid}
            initialName={saveListContext ? `${saveListContext.supermarketName} Shopping List` : ''}
            initialSupermarketId={saveListContext?.supermarketId || ''}
            initialSupermarketName={saveListContext?.supermarketName || ''}
            initialNotificationDate={null}
            initialItems={saveListContext?.products || []}
            onHide={() => setSaveListContext(null)}
            onSaved={(message) => {
              setSaveListContext(null);
              showNotification({
                type: 'success',
                title: 'Shopping List Saved',
                message,
                duration: 5000
              });
            }}
          />
        )}

        <PickupVerificationQrModal
          show={pickupQrCartInstanceId !== null}
          onHide={() => setPickupQrCartInstanceId(null)}
          cartInstanceId={pickupQrCartInstanceId || ''}
          userName={userName || currentUser?.displayName || 'User'}
        />

        {/* Add spacing before footer */}
        <div className="mb-5"></div>
      </Container>
    </Layout>
  );
}
